// A receipt, made ready for the reader (Add expense, v4.6.0; build pack p17:
// "The browser shrinks photos to 2000 px or less first").
//
// A photo is decoded and redrawn as a JPEG no more than 2000 px on its long
// edge; a PDF has its first page rendered the same way with the PDF.js Track
// already ships. Only this JPEG goes to readReceipt — the ORIGINAL file is
// what fileReceipt files when the claim is saved, untouched.
//
// A file the browser can't decode (a HEIC on Chrome, a damaged PDF) throws;
// the form then says it couldn't read it and stays usable by hand.
export const MAX_EDGE = 2000;
const QUALITY = 0.85;

export const receiptKind = (file) => {
  const t = String((file && file.type) || '').toLowerCase();
  const ext = String((file && file.name) || '').split('.').pop().toLowerCase();
  if (t === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (t.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif'].includes(ext)) return 'image';
  return null;
};

/** The scale that brings w × h within MAX_EDGE on its long edge (never enlarges). */
export const fitScale = (w, h, max = MAX_EDGE) => Math.min(1, max / Math.max(1, w, h));

let pdfjsMod = null;
async function pdfjs() {
  if (!pdfjsMod) {
    pdfjsMod = await import('/vendor/pdfjs/build/pdf.mjs');
    pdfjsMod.GlobalWorkerOptions.workerSrc = '/vendor/pdfjs/build/pdf.worker.mjs';
  }
  return pdfjsMod;
}

function canvasToJpeg(canvas) {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not draw the receipt.'))), 'image/jpeg', QUALITY));
}
const blobBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('Could not read the receipt.'));
  r.readAsDataURL(blob);
});

async function drawImage(file) {
  const bmp = await createImageBitmap(file);
  const k = fitScale(bmp.width, bmp.height);
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(bmp.width * k)); c.height = Math.max(1, Math.round(bmp.height * k));
  const ctx = c.getContext('2d');
  ctx.fillStyle = 'white'; ctx.fillRect(0, 0, c.width, c.height);   // a transparent PNG reads as white paper
  ctx.drawImage(bmp, 0, 0, c.width, c.height);
  if (bmp.close) bmp.close();
  return c;
}

async function drawPdfPage(file) {
  const lib = await pdfjs();
  const bytes = new Uint8Array(await file.arrayBuffer());
  const doc = await lib.getDocument({ data: bytes, standardFontDataUrl: '/vendor/pdfjs/web/standard_fonts/' }).promise;
  try {
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const vp = page.getViewport({ scale: Math.min(4, MAX_EDGE / Math.max(base.width, base.height)) });
    const c = document.createElement('canvas');
    c.width = Math.round(vp.width); c.height = Math.round(vp.height);
    const ctx = c.getContext('2d');
    ctx.fillStyle = 'white'; ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    return c;
  } finally { doc.destroy(); }
}

/** File → { blob, base64, url, width, height } — a JPEG of the receipt, ≤ 2000 px. */
export async function receiptImage(file) {
  const kind = receiptKind(file);
  if (!kind) throw new Error('A receipt must be a PDF or a photo.');
  const canvas = kind === 'pdf' ? await drawPdfPage(file) : await drawImage(file);
  const blob = await canvasToJpeg(canvas);
  return { blob, base64: await blobBase64(blob), url: URL.createObjectURL(blob), width: canvas.width, height: canvas.height };
}
